import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {Branch} from '@store/branches/branch.types';
import {MatCardActions, MatCardContent, MatCardTitle} from '@angular/material/card';
import {MatButton} from '@angular/material/button';
import {RvFormsModule} from '../../../../shared/form-fields/rv-forms.module';
import {SelectPipeModule} from '../../../../shared/pipes/select-pipe/select-pipe.module';
import {convertToSelectOptions} from '@store/store.helpers';
import {Store} from '@ngrx/store';
import {CommonValidators} from '@store/common/common.validators';
import {PatronActions} from '@store/patron/patron.actions';
import {CatalogRecord} from '@raven';

export interface PlaceHoldBranchSelectionDialogData {
  catalogRecord: CatalogRecord;
  branches: Branch[];
  defaultBranchId: number;
}

@Component({
  selector: 'rn-place-hold-branch-selection',
  template: `
    <div mat-dialog-content class="dialog-container">
      <mat-card-title class="mx-6 mt-6"><h1 class="place-hold-title">Place Hold</h1></mat-card-title>
      <mat-card-content>
        <div class="mat-subtitle-2">{{ catalogRecord.title }}</div>
        <div class="mat-caption">{{ catalogRecord.materialType?.description }}</div>
        <form [formGroup]="pickupBranchForm" novalidate>
          <rv-select-form-field fieldName="pickupBranch" overrideLabel="Pickup Location" [formGroup]="pickupBranchForm"
                                [options]="branchSelectOptions" [includeUndefinedOption]="false"/>
          <mat-card-actions align="end" class="button-row-right">
            <button mat-stroked-button class="ButtonSmallBlack save-button-group button-cancel" (click)="cancel()"
                    data-cy="on-hold-button">Cancel
            </button>
            <button mat-raised-button color="primary" class="ButtonSmallBlack save-button-group save-button"
                    [disabled]="!this.pickupBranchForm.valid" (click)="onSubmit()">Place Hold
            </button>
          </mat-card-actions>
        </form>
      </mat-card-content>
    </div>
  `,
  styles: `
    .dialog-container {
      max-width: 31.3rem;
    }

    .place-hold-title {
      margin-top: 0.5rem;
    }

    .mat-caption {
      margin-top: 0.2rem;
      margin-bottom: 1.25rem;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatCardActions, MatCardContent, MatCardTitle, MatDialogContent, ReactiveFormsModule, RvFormsModule, SelectPipeModule]
})
export class PlaceHoldBranchSelectionDialog {
  public readonly dialogData: PlaceHoldBranchSelectionDialogData = inject(MAT_DIALOG_DATA);
  public readonly catalogRecord = this.dialogData.catalogRecord;
  public readonly branchSelectOptions = convertToSelectOptions(this.dialogData.branches, 'name', 'id')
  private readonly dialogRef: MatDialogRef<PlaceHoldBranchSelectionDialog> = inject(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  public readonly pickupBranchForm: FormGroup = this.fb.group({
    pickupBranch: [this.dialogData.defaultBranchId, {validators: [Validators.required, CommonValidators.requiredIdSelect]}],
  });

  cancel(): void {
    this.dialogRef.close(false);
  }

  onSubmit(): void {
    this.store.dispatch(PatronActions.placeHold(this.catalogRecord, this.pickupBranchForm.value.pickupBranch));
    this.dialogRef.close(this.pickupBranchForm.value.pickupBranch);
  }
}
